import { useEffect, useRef } from "react";
import { Contact, Download, Printer } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useLang } from "@/context/LanguageContext";
import { PHONE_DISPLAY, CONTACT_EMAIL } from "@/lib/contact";

const W = 1050;
const H = 600;
const SITE = "www.smartelecomadvisor.be";

export const BusinessCard = () => {
  const { t } = useLang();
  const b = t.dash.bizCard;
  const canvasRef = useRef(null);
  const qrRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const drawAll = (bubbles) => {
      if (cancelled || !canvasRef.current) return;
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(0, 0, W, H);
      const g = ctx.createRadialGradient(W - 180, 120, 30, W - 180, 120, 700);
      g.addColorStop(0, "rgba(255,121,0,0.25)");
      g.addColorStop(1, "rgba(255,121,0,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "#FF7900";
      ctx.fillRect(0, 0, 14, H);

      ctx.textAlign = "left";
      ctx.fillStyle = "#ffffff";
      ctx.font = "700 56px Arial";
      ctx.fillText("Smart Telecom Advisor", 64, 100);
      ctx.fillStyle = "#FF7900";
      ctx.font = "600 34px Arial";
      ctx.fillText("Sam — " + b.role, 64, 155);

      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.font = "500 32px Arial";
      ctx.fillText("📱  " + PHONE_DISPLAY, 64, 252);
      ctx.fillText("✉  " + CONTACT_EMAIL, 64, 320);

      const ly = 388;
      ctx.fillStyle = "#FF7900";
      ctx.beginPath();
      ctx.roundRect(64, ly, 64, 64, 8);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "700 18px Arial";
      ctx.fillText("orange", 72, ly + 54);
      ctx.font = "400 12px Arial";
      ctx.fillText("™", 112, ly + 20);

      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.roundRect(144, ly, 180, 64, 8);
      ctx.fill();
      let ex = 168;
      if (bubbles) {
        const bw = (bubbles.width / bubbles.height) * 42;
        ctx.drawImage(bubbles, 156, ly + 11, bw, 42);
        ex = 156 + bw + 8;
      }
      ctx.fillStyle = "#e2001a";
      ctx.font = "700 32px Arial";
      ctx.fillText("Eneco", ex, ly + 44);

      ctx.fillStyle = "#FF7900";
      ctx.font = "700 42px Arial";
      ctx.fillText(SITE, 64, 512);
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.font = "400 26px Arial";
      ctx.fillText(b.tagline, 64, 556);

      const qrCanvas = qrRef.current?.querySelector("canvas");
      const qs = 300;
      const qx = W - qs - 70;
      const qy = (H - qs) / 2 - 20;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.roundRect(qx - 22, qy - 22, qs + 44, qs + 44, 20);
      ctx.fill();
      if (qrCanvas) ctx.drawImage(qrCanvas, qx, qy, qs, qs);
      ctx.textAlign = "center";
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.font = "600 26px Arial";
      ctx.fillText(b.scan, qx + qs / 2, qy + qs + 70);
    };

    const bubbles = new Image();
    bubbles.onload = () => drawAll(bubbles);
    bubbles.onerror = () => drawAll(null);
    bubbles.src = "/eneco-bubbles.png";
    return () => { cancelled = true; };
  }, [b]);

  const download = () => {
    const a = document.createElement("a");
    a.href = canvasRef.current.toDataURL("image/png");
    a.download = "carte-visite-smart-telecom-advisor.png";
    a.click();
  };

  const print = () => {
    const dataUrl = canvasRef.current.toDataURL("image/png");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>Carte de visite</title><style>body{margin:0;display:flex;justify-content:center}img{width:85mm;height:auto}</style></head><body><img src="${dataUrl}" onload="window.print()"/></body></html>`);
    w.document.close();
  };

  return (
    <div className="bg-[#111111] border border-white/10 p-6 md:p-8 mb-10" data-testid="business-card-panel">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
        <Contact className="w-4 h-4 text-[#FF7900]" /> {b.title}
      </p>
      <p className="text-xs text-white/40 mb-5">{b.hint}</p>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="flex-1 flex flex-wrap gap-3">
          <button
            onClick={download}
            data-testid="business-card-download"
            className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            <Download className="w-4 h-4" /> {b.download}
          </button>
          <button
            onClick={print}
            data-testid="business-card-print"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-[#FF7900] hover:text-[#FF7900] text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            <Printer className="w-4 h-4" /> {b.print}
          </button>
        </div>
        <div ref={qrRef} className="hidden">
          <QRCodeCanvas value={`https://${SITE}?utm_source=card`} size={300} fgColor="#000000" bgColor="#FFFFFF" />
        </div>
        <canvas ref={canvasRef} width={W} height={H} data-testid="business-card-canvas" className="w-[340px] rounded-xl border border-white/10 shrink-0 shadow-2xl" />
      </div>
    </div>
  );
};
